import { FC, ChangeEvent, useMemo } from 'react'
import { useTranslation } from 'next-i18next/pages'
import Image from '@corratech/pylot-image'
import Link from 'next/link'
import { Tooltip } from '@corratech/pylot-ui'
import { DeleteWishlistItem } from '../WishlistToolbar'
import { ProductPrice } from '@components/common/ProductPrice/ProductPrice'
import { usePrice } from '@corratech/pylot-price'
import {
    Maybe,
    WishlistItemInterface,
    ConfigurableWishlistItem
} from '@pylot-data/pylotschema'
import { WishlistManager } from '../Hooks/WishlistActionTypes'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import s from '@pagestyles/Wishlist.module.scss'

export type Props = {
    item: Maybe<ConfigurableWishlistItem & WishlistItemInterface>
    wishlistManager: WishlistManager
    wishlistId: Maybe<string> | undefined
}

export const WishlistProductGrid: FC<Props> = ({
    item,
    wishlistManager,
    wishlistId
}) => {
    const { t } = useTranslation(['wishlist'])
    const { wishlistState, addSelected, removeSelected } = wishlistManager
    const config = useStoreConfig()
    const { total } = usePrice(item?.product?.price_range!)
    const urlSuffix = config.base?.url?.model?.product?.suffix
    const productPriceRange = item?.product?.price_range!

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            addSelected([item?.id!])
        } else {
            removeSelected(item?.id!)
        }
    }

    const toolTipContent = useMemo(() => {
        const configurableOption = item?.configurable_options

        if (!configurableOption?.length) return null

        return (
            <Tooltip heading={t('wishlist|See Details')} direction="right">
                {configurableOption?.map((option, key) => (
                    <div className="text-left mb-1" key={key}>
                        <span className="font-bold">
                            {option?.option_label} :{' '}
                        </span>
                        <span> {option?.value_label}</span>
                    </div>
                ))}
            </Tooltip>
        )
    }, [item?.configurable_options, t])

    return (
        <div className="product-item-block">
            <div className={s['product-image-block']}>
                <Link href={`/producto/${item?.product?.url_key}${urlSuffix}`}>
                    <div className={s['wishlist-product-img-link']}>
                        <Image
                            src={String(item?.product?.small_image?.url)}
                            alt={String(item?.product?.name)}
                            width={320}
                            height={320}
                            className="product-img"
                        />
                    </div>
                </Link>
                <DeleteWishlistItem item={item} wishlistId={wishlistId} />
                <div className={s['product-select-action']}>
                    <div>
                        <label
                            className="sr-only"
                            htmlFor={`wishlist-item${item?.id}`}
                        >
                            <span className="hidden">
                                {t('wishlist|Wishlist Item')}
                            </span>
                        </label>
                        <input
                            type="checkbox"
                            className="form-checkbox"
                            id={`wishlist-item${item?.id}`}
                            onChange={handleChange}
                            checked={wishlistState.selected.includes(
                                String(item?.id)
                            )}
                        />
                    </div>
                </div>
                <Link href={`/producto/${item?.product?.url_key}${urlSuffix}`}>
                    <h2 className={s['product-name']}>{item?.product?.name}</h2>
                </Link>
                {total && (
                    <ProductPrice
                        className={String(item?.__typename).toLowerCase()}
                        priceRange={productPriceRange}
                    />
                )}
                {toolTipContent}
            </div>
        </div>
    )
}
